/**
 * Minimal safe expression evaluator for workflow formulas.
 * Supports arithmetic, comparisons, and identifiers from the payload scope.
 * Falls back to Function when expr is a simple arithmetic/logical expression over scope keys.
 */
export class ExpressionEvaluator {
  private readonly compiled = new Map<string, (...args: unknown[]) => unknown>();

  evaluate(expression: string, payload: Record<string, unknown>): unknown {
    const trimmed = expression.trim();
    if (!trimmed) return undefined;

    const scope = toScope(payload);
    try {
      return evaluateArithmetic(trimmed, scope);
    } catch {
      try {
        const keys = Object.keys(scope);
        const cacheKey = `${trimmed}\0${keys.join('\0')}`;
        let fn = this.compiled.get(cacheKey);
        if (!fn) {
          const values = keys.map((k) => scope[k]);
          // eslint-disable-next-line no-new-func
          fn = new Function(...keys, `"use strict"; return (${trimmed});`) as (
            ...args: unknown[]
          ) => unknown;
          this.compiled.set(cacheKey, fn);
          return fn(...values);
        }
        return fn(...keys.map((k) => scope[k]));
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Expression evaluation failed',
        );
      }
    }
  }
}

function toScope(payload: Record<string, unknown>): Record<string, unknown> {
  const scope: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    const num = Number(value);
    scope[key] =
      typeof value === 'string' && value.trim() !== '' && Number.isFinite(num) ? num : value;
  }
  return scope;
}

/** Tiny recursive-descent arithmetic for EMI-style formulas without a dependency. */
function evaluateArithmetic(expr: string, scope: Record<string, unknown>): unknown {
  let i = 0;

  function peek(): string {
    return expr[i] ?? '';
  }
  function consume(): string {
    return expr[i++] ?? '';
  }
  function skip(): void {
    while (/\s/.test(peek())) i++;
  }

  function parsePrimary(): number {
    skip();
    if (peek() === '(') {
      consume();
      const v = parseExpr();
      skip();
      if (peek() !== ')') throw new Error('Expected )');
      consume();
      return v;
    }
    if (peek() === '-') {
      consume();
      return -parsePrimary();
    }
    if (/[0-9.]/.test(peek())) {
      let num = '';
      while (/[0-9.]/.test(peek())) num += consume();
      return Number(num);
    }
    if (/[A-Za-z_]/.test(peek())) {
      let id = '';
      while (/[A-Za-z0-9_]/.test(peek())) id += consume();
      const val = scope[id];
      if (val === undefined || val === null) return 0;
      const n = Number(val);
      if (!Number.isFinite(n)) throw new Error(`Non-numeric identifier: ${id}`);
      return n;
    }
    throw new Error(`Unexpected token at ${i}`);
  }

  function parseMul(): number {
    let left = parsePrimary();
    for (;;) {
      skip();
      if (peek() === '*') {
        consume();
        left *= parsePrimary();
      } else if (peek() === '/') {
        consume();
        left /= parsePrimary();
      } else if (peek() === '%') {
        consume();
        left %= parsePrimary();
      } else break;
    }
    return left;
  }

  function parseExpr(): number {
    let left = parseMul();
    for (;;) {
      skip();
      if (peek() === '+') {
        consume();
        left += parseMul();
      } else if (peek() === '-') {
        consume();
        left -= parseMul();
      } else break;
    }
    return left;
  }

  const result = parseExpr();
  skip();
  if (i < expr.length) {
    // Not a pure arithmetic expression — let Function fallback handle it.
    throw new Error('Not pure arithmetic');
  }
  return result;
}
